import React, { useEffect } from "react";
import {
  Grid,
  TextField,
  MenuItem,
  Button,
  Icon,
} from "@mui/material";
import { FastField, useFormikContext } from "formik";
import DropdownSelector from "../../atoms/Common/DropdownSelector";
import {
  getPurchaseOrderList,
} from "../../api/purchaseOrders";
import { getProviderDetail, getProviderList } from "../../api/providers";
import { getCatalogue } from "../../api/common/catalogues.ts";
import { getCurrencyList } from "../../api/common/currencies";
import { useSnack } from "../../services/SnakbarProvider";
import { handleError } from "../../services/errorHandler";
import dayjs from "dayjs";
import LGAutocomplete from "../../atoms/Common/LGAutocomplete.tsx";

const RAHeader = ({ formMode }) => {
  const { values, setFieldValue, errors, touched } = useFormikContext();

  const { openSnack } = useSnack();

  const handleProviderChange = async (value) => {
    const completeProviderData = await fetchProvider(value);
    setFieldValue("selectedProvider", completeProviderData);
    setFieldValue("receiptAcknowledgement.providerId", value ? value.id : "");
  }


  const fetchProvider = async (value) => {
    const provider = await getProviderDetail(value.id, values.receiptAcknowledgement?.companyId);
    return provider.data;
  }

  const handlePurchaseOrderChange = (value) => {
    setFieldValue("selectedPurchaseOrder", value);
    setFieldValue("receiptAcknowledgement.purchaseOrderId", value ? value.id : "");
    setFieldValue("receiptAcknowledgement.providerId", value ? value.providerId : "");
    setFieldValue("selectedProvider", value ? value.provider : "");
    setFieldValue(
      "receiptAcknowledgement.currencyId",
      value?.currencyId ?? value?.currency?.id ?? 1
    );
  }

  const handleRATypeChange = (event, value) => {
    setFieldValue("selectedRAType", value);
    setFieldValue("receiptAcknowledgement.raTypeId", value?.id || "");
    setFieldValue("receiptAcknowledgement.raType", value);
  };

  const isCompany1 = values.receiptAcknowledgement.companyId === 1;

  // Set default month on mount if not already set
  useEffect(() => {
    if (!values.receiptAcknowledgement.month) {
      const currentMonth = dayjs().month() + 1;
      setFieldValue("receiptAcknowledgement.month", currentMonth);
    }
  }, []);

  // Set default year for company 1
  useEffect(() => {
    if (isCompany1 && !values.receiptAcknowledgement.year) {
      setFieldValue("receiptAcknowledgement.year", dayjs().year());
    }
  }, [isCompany1]);

  // Compute month number based on selection
  const getMonthValue = (selection) => {
    if (selection === "current") {
      return dayjs().month() + 1;
    } else if (selection === "previous") {
      const previousMonth = dayjs().subtract(1, "month");
      return previousMonth.month() + 1;
    }
    return selection; // If it's already a number, return as is
  };

  // Get the display value for the dropdown
  const getMonthDisplayValue = () => {
    const currentMonth = dayjs().month() + 1;
    const previousMonth = dayjs().subtract(1, "month").month() + 1;
    const storedMonth = values.receiptAcknowledgement.month;

    if (storedMonth === currentMonth) {
      return "current";
    } else if (storedMonth === previousMonth) {
      return "previous";
    }
    // If stored month doesn't match current or previous, default to current
    return "current";
  };

  const handleMonthChange = (event) => {
    const selection = event.target.value;
    const monthValue = getMonthValue(selection);
    setFieldValue("receiptAcknowledgement.month", monthValue);
  };

  const handleMonthYearChange = (field) => (event) => {
    const value = parseInt(event.target.value, 10);
    setFieldValue(`receiptAcknowledgement.${field}`, value);
  };

  const handleDataUpdate = async () => {
    try {
      const RAItems = generateRAItems(values.selectedPurchaseOrder.items);
      setFieldValue("items", RAItems ?? []);

      // Set provider information
      if (values.selectedPurchaseOrder.providerId) {
        setFieldValue("receiptAcknowledgement.providerId", values.selectedPurchaseOrder.providerId);

        // Fetch provider details to populate the selectedProvider field
        const providerData = await getProviderDetail(
          values.selectedPurchaseOrder.providerId,
          values.receiptAcknowledgement.companyId
        );

        if (providerData?.data) {
          setFieldValue("selectedProvider", providerData.data);
        }
      }

      // Set beneficiary
      if (values.selectedPurchaseOrder.requestedFrom) {
        setFieldValue("receiptAcknowledgement.beneficiary", values.selectedPurchaseOrder.requestedFrom);
      }

      const poCurrencyId =
        values.selectedPurchaseOrder.currencyId ??
        values.selectedPurchaseOrder.currency?.id;
      if (poCurrencyId) {
        setFieldValue("receiptAcknowledgement.currencyId", poCurrencyId);
      }

    } catch (e) {
      openSnack(handleError(e), "error");
    }
  };

  const generateRAItems = (POItems) => {
    const groupedData = POItems.reduce((acc, item) => {
      const expenseAccountId = item.expenseAccountId;

      let group = acc.find((obj) => obj.expenseAccountId === expenseAccountId);
      if (!group) {
        group = {
          expenseAccountId: expenseAccountId,
          providerAccountId: "",
          amount: 0,
          taxRate: 0,
        };
        acc.push(group);
      }

      group.amount += item.subtotal;
      group.taxRate = item.taxRate;

      return acc;
    }, []);

    return groupedData;
  };

  const handleSelectPurchaseOrder = (event, value) => {
    setFieldValue(
      "receiptAcknowledgement.purchaseOrderId",
      value ? value.id : ""
    );
  };

  useEffect(() => {
    var creditDays = 0;
    if (values.selectedProvider?.creditDays) {
      creditDays = values.selectedProvider.creditDays;
    }
    if (formMode === "insert" && creditDays > 0) {
      const invoiceDate =
        dayjs(values.receiptAcknowledgement.invoiceDate) ?? dayjs();

      // Add credit days
      const dateWithCredit = invoiceDate.add(
        creditDays,
        "day"
      );

      // Find next Wednesday (3 is Wednesday in dayjs)
      const finalDate =
        dateWithCredit.day() <= 3
          ? dateWithCredit.day(3)
          : dateWithCredit.add(1, "week").day(3);

      // Format date as YYYY-MM-DD
      setFieldValue("receiptAcknowledgement.paymentDate", finalDate.format("YYYY-MM-DD"));
    }
  }, [values.selectedProvider, values.receiptAcknowledgement.invoiceDate]);

  const monthOptions = [
    { value: "current", name: "Mes Actual" },
    { value: "previous", name: "Mes Anterior" },
  ];

  const monthNames = [
    "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
    "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"
  ];

  const yearOptions = Array.from({ length: 5 }, (_, i) => dayjs().year() - i);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={9}>
        <LGAutocomplete
          fetchFunction={getPurchaseOrderList}
          queryParams={{
            companyId: values.receiptAcknowledgement.companyId,
            showApproved: true,
          }}
          optionLabel="code"
          value={values.selectedPurchaseOrder || null}
          onChange={(e, value) => handlePurchaseOrderChange(value)}
          label="Orden de compra"
        />
      </Grid>
      <Grid item xs={3}>
        <Button
          fullWidth
          sx={{ height: "100%" }}
          variant="contained"
          endIcon={<Icon>system_update_alt</Icon>}
          onClick={handleDataUpdate}
          disabled={!values.receiptAcknowledgement.purchaseOrderId}
        >
          Cargar datos
        </Button>
      </Grid>
      <Grid item xs={12} md={6}>
        {!values.selectedPurchaseOrder && formMode === "insert" && <LGAutocomplete
          id="receiptAcknowledgement.providerId"
          name="receiptAcknowledgement.providerId"
          fetchFunction={getProviderList}
          queryParams={{
            companyId: values.receiptAcknowledgement.companyId,
          }}
          optionLabel="code"
          onChange={(e, value) => {
            handleProviderChange(value);
          }}
          label="Proveedor"
          error={touched?.receiptAcknowledgement?.providerId && Boolean(errors?.receiptAcknowledgement?.providerId)}
          helperText={touched?.receiptAcknowledgement?.providerId && errors?.receiptAcknowledgement?.providerId}
        />
        }
        {(values.selectedPurchaseOrder || formMode == "edit") && <TextField
          fullWidth
          value={values.selectedProvider?.code || values.selectedPurchaseOrder?.provider?.code || ""}
          label="Proveedor"
          disabled
        />}
      </Grid>
      <Grid item xs={12} md={isCompany1 ? 2 : 3}>
        <FastField name="receiptAcknowledgement.raTypeId"
          label="Tipo de Contrarrecibo"
          as={DropdownSelector}
          onChange={handleRATypeChange}
          fetchFunction={getCatalogue}
          fetchParams={{ CatalogueName: "RA_TYPE" }}
        />
      </Grid>
      <Grid item xs={12} md={isCompany1 ? 2 : 3}>
        {isCompany1 ? (
          <FastField name="receiptAcknowledgement.month">
            {({ field }) => (
              <TextField
                select
                fullWidth
                label="Mes"
                value={values.receiptAcknowledgement.month ?? ""}
                onChange={handleMonthYearChange("month")}
                variant="outlined"
              >
                {monthNames.map((name, idx) => (
                  <MenuItem key={idx} value={idx + 1}>
                    {name}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </FastField>
        ) : (
          <FastField name="receiptAcknowledgement.month">
            {({ field }) => (
              <TextField
                select
                fullWidth
                label="Mes"
                value={getMonthDisplayValue()}
                onChange={handleMonthChange}
                variant="outlined"
              >
                {monthOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </FastField>
        )}
      </Grid>
      {isCompany1 && (
        <Grid item xs={12} md={2}>
          <FastField name="receiptAcknowledgement.year">
            {({ field }) => (
              <TextField
                select
                fullWidth
                label="Año"
                value={values.receiptAcknowledgement.year ?? ""}
                onChange={handleMonthYearChange("year")}
                variant="outlined"
              >
                {yearOptions.map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </FastField>
        </Grid>
      )}
      <Grid item xs={12} md={3}>
        <FastField
          name="receiptAcknowledgement.invoiceNo"
          as={TextField}
          fullWidth
          label="Número de Factura"
          variant="outlined"
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <FastField name="receiptAcknowledgement.paymentMethodId"
          label="Método de Pago"
          as={DropdownSelector}
          fetchFunction={getCatalogue}
          fetchParams={{ CatalogueName: "PAYMENT_METHOD" }}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <DropdownSelector
          name="receiptAcknowledgement.currencyId"
          label="Moneda"
          fetchFunction={getCurrencyList}
          optionLabel="symbol"
          disabled={Boolean(values.selectedPurchaseOrder || values.receiptAcknowledgement.purchaseOrderId)}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <FastField name="receiptAcknowledgement.invoiceDate">
          {({ field }) => (
            <TextField
              fullWidth
              label="Fecha de Factura"
              type="date"
              {...field}
              InputLabelProps={{
                shrink: true,
              }}
              variant="outlined"
            />
          )}
        </FastField>
      </Grid>
      <Grid item xs={12} md={6}>
        <FastField name="receiptAcknowledgement.beneficiary">
          {({ field }) => (
            <TextField
              fullWidth
              label="Quien lo pide"
              {...field}
              variant="outlined"
            />
          )}
        </FastField>
      </Grid>
    </Grid>
  );
};

export default RAHeader;
